/**
 * Image Upload Constants and Configuration
 * Centralized configuration for image validation rules and error messages
 */

/**
 * Allowed file extensions for image uploads
 * Only common, widely-supported formats that work with Excel exports
 */
export const ALLOWED_FORMATS = ['jpg', 'jpeg', 'png'];

/**
 * Allowed MIME types for image uploads
 * Corresponds to the allowed file extensions for security
 */
export const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

/**
 * Maximum file size for image uploads
 * 4MB in bytes (4 * 1024 * 1024 = 4,194,304 bytes)
 */
export const MAX_FILE_SIZE = 4 * 1024 * 1024;

/**
 * Maximum file size in MB for display purposes
 */
export const MAX_FILE_SIZE_MB = 4;

/**
 * User-friendly error messages for validation failures
 */
export const ERROR_MESSAGES = {
  FORMAT: 'Please upload JPG, JPEG, or PNG files only',
  SIZE: 'Please upload files under 4MB',
  SIZE_WITH_CURRENT: (currentSizeMB) => `File size is ${currentSizeMB}MB. Please upload files under 4MB`,
  NO_FILE: 'No file provided',
  MULTIPLE_ERRORS: 'Multiple validation errors occurred',
  INVALID_FILE: 'Invalid file'
};

/**
 * File validation configuration object
 * Combines all validation rules in one place
 */
export const IMAGE_VALIDATION_CONFIG = {
  allowedFormats: ALLOWED_FORMATS,
  allowedMimeTypes: ALLOWED_MIME_TYPES,
  maxFileSize: MAX_FILE_SIZE,
  maxFileSizeMB: MAX_FILE_SIZE_MB,
  errorMessages: ERROR_MESSAGES
};

/**
 * Image upload configuration object (alias for component compatibility)
 * Provides the same data with component-expected property names
 */
export const IMAGE_UPLOAD_CONFIG = {
  acceptedFormats: ALLOWED_FORMATS,
  maxFileSize: MAX_FILE_SIZE,
  allowedMimeTypes: ALLOWED_MIME_TYPES,
  errorMessages: ERROR_MESSAGES
};

/**
 * Utility function to check if a file extension is allowed
 * @param {string} extension - File extension to check (without dot)
 * @returns {boolean} - True if extension is allowed
 */
export const isAllowedFormat = (extension) => {
  return ALLOWED_FORMATS.includes(extension.toLowerCase());
};

/**
 * Utility function to check if a MIME type is allowed
 * @param {string} mimeType - MIME type to check
 * @returns {boolean} - True if MIME type is allowed
 */
export const isAllowedMimeType = (mimeType) => {
  return ALLOWED_MIME_TYPES.includes(mimeType.toLowerCase());
};

/**
 * Utility function to format file size in MB
 * @param {number} sizeInBytes - File size in bytes
 * @returns {string} - Formatted size string (e.g., "2.5MB")
 */
export const formatFileSize = (sizeInBytes) => {
  const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(1);
  return `${sizeInMB}MB`;
};
